import React, { useCallback, useMemo } from 'react'

import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { StaffAIChatSessionPreviewFragment } from 'types/graphql'

import {
  useSessionPreviewTitle,
  useStaffAIAssistant,
} from 'src/components/StaffAIAssistant/StaffAIAssistantContext'
import { useGetStaffAIChatSessionsQuery } from 'src/fetch/staffAIChat'
import { Button, HStack, Icon, Input, T, VStack } from 'src/govwell-ui'

// Extend dayjs with relative time plugin
dayjs.extend(relativeTime)

/**
 * Format a timestamp as relative time (e.g., "2 hours ago", "Yesterday")
 */
const formatRelativeTime = (date: string | Date): string => {
  const dayjsDate = dayjs(date)
  const now = dayjs()

  // If it's today, show relative time
  if (dayjsDate.isSame(now, 'day')) {
    return dayjsDate.fromNow()
  }

  // If it's yesterday
  if (dayjsDate.isSame(now.subtract(1, 'day'), 'day')) {
    return 'Yesterday'
  }

  // If it's within the last week
  if (dayjsDate.isAfter(now.subtract(7, 'day'))) {
    return dayjsDate.format('dddd')
  }

  // Otherwise show the date
  return dayjsDate.format('MMM D, YYYY')
}

type ConversationItemProps = {
  session: StaffAIChatSessionPreviewFragment
}

const ConversationItem = ({ session }: ConversationItemProps) => {
  const context = useStaffAIAssistant()
  const title = useSessionPreviewTitle(session)

  const isActive = context?.activeSessionId === session.id

  const timestamp = useMemo(() => {
    const date = session.previewChat?.questionCreatedAt ?? session.startedAt
    return formatRelativeTime(date)
  }, [session.previewChat?.questionCreatedAt, session.startedAt])

  const ariaLabel = useMemo(() => `Open conversation: ${title}, ${timestamp}`, [title, timestamp])

  const handleClick = useCallback(() => {
    if (!context) {
      return
    }
    context.setActiveSessionId(session.id)
    context.setIsLoadingSession(true)
  }, [context, session.id])

  return (
    <Button
      variant="text"
      onClick={handleClick}
      ariaLabel={ariaLabel}
      className={`!h-auto w-full !justify-start !gap-0.5 !rounded-lg !px-3 !py-2.5 text-left ${
        isActive
          ? 'bg-neutral-200 text-neutral-900'
          : 'bg-transparent text-neutral-700 hover:bg-neutral-100'
      }`}
    >
      <VStack gap={0} align="start" className="w-full">
        <T.BodySmall className={`line-clamp-2 text-left ${isActive ? 'font-medium' : ''}`}>
          {title}
        </T.BodySmall>
        <T.BodySmall className="text-neutral-500">{timestamp}</T.BodySmall>
      </VStack>
    </Button>
  )
}

const ConversationItemMemo = React.memo(ConversationItem)

type Props = {
  searchQuery: string
  onSearchQueryChange: (query: string) => void
}

const StaffAIChatSidebar = ({ searchQuery, onSearchQueryChange }: Props) => {
  const context = useStaffAIAssistant()
  const { sessions, isLoading } = useGetStaffAIChatSessionsQuery()

  const handleNewConversation = useCallback(() => {
    context?.startNewConversation()
  }, [context])

  // Filter sessions based on search query
  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) {
      return sessions
    }
    const query = searchQuery.toLowerCase()
    return sessions.filter((session) => {
      const question = session.previewChat?.question?.toLowerCase() ?? ''
      return question.includes(query)
    })
  }, [sessions, searchQuery])

  // Group sessions by time period
  const groupedSessions = useMemo(() => {
    const starred: StaffAIChatSessionPreviewFragment[] = []
    const recent: StaffAIChatSessionPreviewFragment[] = []

    const now = dayjs()
    const weekAgo = now.subtract(7, 'day')

    for (const session of filteredSessions) {
      const sessionDate = dayjs(session.previewChat?.questionCreatedAt ?? session.startedAt)
      if (sessionDate.isAfter(weekAgo)) {
        recent.push(session)
      } else {
        // For now, older sessions are shown in "recent" too
        recent.push(session)
      }
    }

    return { starred, recent }
  }, [filteredSessions])

  return (
    <div className="flex h-full w-72 shrink-0 flex-col border-r border-neutral-200 bg-white">
      {/* New Conversation Button */}
      <div className="shrink-0 p-3">
        <Button
          variant="primary"
          onClick={handleNewConversation}
          className="w-full justify-center"
          icon="FaPlus"
        >
          New Conversation
        </Button>
      </div>

      {/* Search Input */}
      <div className="shrink-0 px-3 pb-3">
        <Input
          ariaLabel="Search conversations"
          placeholder="Search conversations..."
          value={searchQuery}
          onValueChange={onSearchQueryChange}
          prefixIcon={faMagnifyingGlass}
        />
      </div>

      {/* Conversations List */}
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <T.BodySmall className="text-neutral-500">Loading conversations...</T.BodySmall>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <T.BodySmall className="text-neutral-500">
              {searchQuery ? 'No matching conversations' : 'No past conversations'}
            </T.BodySmall>
          </div>
        ) : (
          <VStack gap={1} align="stretch">
            {groupedSessions.recent.length > 0 && (
              <>
                <HStack gap={1} className="mt-2 mb-1 px-1">
                  <Icon icon="FaClock" className="h-3 w-3 text-neutral-500" />
                  <T.Label className="text-neutral-500">Recent</T.Label>
                </HStack>
                {groupedSessions.recent.map((session) => (
                  <ConversationItemMemo key={session.id} session={session} />
                ))}
              </>
            )}
          </VStack>
        )}
      </div>
    </div>
  )
}

export default React.memo(StaffAIChatSidebar)
